import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from './config';
import './Login.css';
import './App.css';

// Password validation rules
const validatePassword = (password) => {
  const rules = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  const isValid = Object.values(rules).every(Boolean);
  return { isValid, rules };
};

export default function RegisterPageWrapper() {
  const navigate = useNavigate();
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  
  // Email verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  const [reg, setReg] = useState({
    username: '', password: '',
    first_name: '', last_name: '', email: '', phone: '', address: '', address_line_2: '', city: '', state: '', zip_code: '', organization: '',
    email_reminders_enabled: true,
    terms_accepted: false
  });
  
  const passwordValidation = validatePassword(reg.password);

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    
    if (!reg.terms_accepted) {
      setRegisterError('You must accept the Terms & Conditions to register.');
      return;
    }
    
    if (!passwordValidation.isValid) {
      setRegisterError('Password does not meet security requirements.');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...reg, isAdmin: false })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      
      // Check if verification is required
      if (data.requiresVerification) {
        setVerificationEmail(reg.email);
        setShowVerification(true);
      } else {
        setRegisterSuccess('Registration successful! You can now log in.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      setRegisterError(err.message);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setVerificationError('');
    setVerificationSuccess('');
    setVerificationLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail, code: verificationCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      
      setVerificationSuccess('Email verified successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setVerificationError(err.message);
    }
    setVerificationLoading(false);
  };
  
  // Resend verification code
  const handleResendVerification = async () => {
    setVerificationError('');
    setVerificationSuccess('');
    setVerificationLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resend code');
      
      setVerificationSuccess('A new verification code has been sent to your email.');
    } catch (err) {
      setVerificationError(err.message);
    }
    setVerificationLoading(false);
  };

  return (
    <div className="centered-page">
      <div className="title">
        <span className="title-the">The</span><span className="title-turf">Turf</span>
        <span className="title-at-gsc">at GSC</span>
        <span className="title-scheduler">Scheduler</span>
      </div>
      <div className="login-container">
        {showVerification ? (
          <form onSubmit={handleVerifyEmail}>
            <h2>Verify Your Email</h2>
            <p className="verification-info">
              We've sent a 6-digit verification code to <strong>{verificationEmail}</strong>
            </p>
            {verificationError && <div className="error">{verificationError}</div>}
            {verificationSuccess && <div className="success">{verificationSuccess}</div>}
            <input
              type="text"
              placeholder="Enter 6-digit code"
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              maxLength={6}
              style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '24px' }}
            />
            <button type="submit" disabled={verificationLoading || verificationCode.length !== 6}>
              {verificationLoading ? 'Verifying...' : 'Verify Email'}
            </button>
            <button 
              type="button" 
              className="link-btn"
              onClick={handleResendVerification}
              disabled={verificationLoading}
            >
              Resend Code
            </button>
            <button 
              type="button" 
              className="link-btn"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </button>
          </form>
        ) : (
        <form onSubmit={handleRegister}>
          <h2>Register</h2>
          {registerError && <div className="error">{registerError}</div>}
          {registerSuccess && <div className="success">{registerSuccess}</div>}
          <input
            type="text"
            placeholder="Username"
            value={reg.username}
            onChange={e => setReg({ ...reg, username: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={reg.password}
            onChange={e => setReg({ ...reg, password: e.target.value })}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            required
          />
          {(passwordFocused || reg.password) && (
            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul>
                <li className={passwordValidation.rules.minLength ? 'valid' : 'invalid'}>
                  {passwordValidation.rules.minLength ? '✓' : '✗'} At least 8 characters
                </li>
                <li className={passwordValidation.rules.hasUppercase ? 'valid' : 'invalid'}>
                  {passwordValidation.rules.hasUppercase ? '✓' : '✗'} One uppercase letter
                </li>
                <li className={passwordValidation.rules.hasLowercase ? 'valid' : 'invalid'}>
                  {passwordValidation.rules.hasLowercase ? '✓' : '✗'} One lowercase letter
                </li>
                <li className={passwordValidation.rules.hasNumber ? 'valid' : 'invalid'}>
                  {passwordValidation.rules.hasNumber ? '✓' : '✗'} One number
                </li>
                <li className={passwordValidation.rules.hasSpecial ? 'valid' : 'invalid'}>
                  {passwordValidation.rules.hasSpecial ? '✓' : '✗'} One special character (!@#$%^&*)
                </li>
              </ul>
            </div>
          )}
          <input
            type="text"
            placeholder="First Name"
            value={reg.first_name}
            onChange={e => setReg({ ...reg, first_name: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Last Name"
            value={reg.last_name}
            onChange={e => setReg({ ...reg, last_name: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={reg.email}
            onChange={e => setReg({ ...reg, email: e.target.value })}
            required
          />
          <input
            type="tel"
            placeholder="Phone"
            value={reg.phone}
            onChange={e => setReg({ ...reg, phone: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Address"
            value={reg.address}
            onChange={e => setReg({ ...reg, address: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Address Line 2 (Suite, Apt, etc.)"
            value={reg.address_line_2}
            onChange={e => setReg({ ...reg, address_line_2: e.target.value })}
          />
          <div className="form-row-inline">
            <input
              type="text"
              placeholder="City"
              value={reg.city}
              onChange={e => setReg({ ...reg, city: e.target.value })}
              required
              className="city-input"
            />
            <select
              value={reg.state}
              onChange={e => setReg({ ...reg, state: e.target.value })}
              required
              className="state-select"
            >
              <option value="">State</option>
              <option value="AL">AL</option>
              <option value="AK">AK</option>
              <option value="AZ">AZ</option>
              <option value="AR">AR</option>
              <option value="CA">CA</option>
              <option value="CO">CO</option>
              <option value="CT">CT</option>
              <option value="DE">DE</option>
              <option value="FL">FL</option>
              <option value="GA">GA</option>
              <option value="HI">HI</option>
              <option value="ID">ID</option>
              <option value="IL">IL</option>
              <option value="IN">IN</option>
              <option value="IA">IA</option>
              <option value="KS">KS</option>
              <option value="KY">KY</option>
              <option value="LA">LA</option>
              <option value="ME">ME</option>
              <option value="MD">MD</option>
              <option value="MA">MA</option>
              <option value="MI">MI</option>
              <option value="MN">MN</option>
              <option value="MS">MS</option>
              <option value="MO">MO</option>
              <option value="MT">MT</option>
              <option value="NE">NE</option>
              <option value="NV">NV</option>
              <option value="NH">NH</option>
              <option value="NJ">NJ</option>
              <option value="NM">NM</option>
              <option value="NY">NY</option>
              <option value="NC">NC</option>
              <option value="ND">ND</option>
              <option value="OH">OH</option>
              <option value="OK">OK</option>
              <option value="OR">OR</option>
              <option value="PA">PA</option>
              <option value="RI">RI</option>
              <option value="SC">SC</option>
              <option value="SD">SD</option>
              <option value="TN">TN</option>
              <option value="TX">TX</option>
              <option value="UT">UT</option>
              <option value="VT">VT</option>
              <option value="VA">VA</option>
              <option value="WA">WA</option>
              <option value="WV">WV</option>
              <option value="WI">WI</option>
              <option value="WY">WY</option>
            </select>
            <input
              type="text"
              placeholder="Zip"
              value={reg.zip_code}
              onChange={e => setReg({ ...reg, zip_code: e.target.value })}
              required
              className="zip-input"
            />
          </div>
          <input
            type="text"
            placeholder="Organization"
            value={reg.organization}
            onChange={e => setReg({ ...reg, organization: e.target.value })}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={reg.email_reminders_enabled}
              onChange={e => setReg({ ...reg, email_reminders_enabled: e.target.checked })}
            />
            Send me email reminders for upcoming bookings
          </label>
          <label className="checkbox-label terms-checkbox">
            <input
              type="checkbox"
              checked={reg.terms_accepted}
              onChange={e => setReg({ ...reg, terms_accepted: e.target.checked })}
              required
            />
            I agree to the{' '}
            <button
              type="button"
              className="terms-link"
              onClick={async () => {
                if (!termsContent) {
                  try {
                    const res = await fetch('/terms-and-conditions.txt');
                    const text = await res.text();
                    setTermsContent(text);
                  } catch (err) {
                    setTermsContent('Unable to load Terms & Conditions. Please try again later.');
                  }
                }
                setShowTerms(true);
              }}
            >
              Terms & Conditions
            </button>
          </label>
          <button type="submit">Register</button>
          <button type="button" className="link-btn" onClick={() => navigate('/login')}>
            Already have an account? Login
          </button>
        </form>
        )}
        
        {showTerms && (
          <div className="modal-overlay" onClick={() => setShowTerms(false)}>
            <div className="modal terms-modal" onClick={e => e.stopPropagation()}>
              <h2>Terms & Conditions</h2>
              <div className="terms-content">
                <pre>{termsContent}</pre>
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowTerms(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
